import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { Loading } from '../../../components';
import { ADDON_LABELS, PLAN_BLURB, PLAN_OPTIONS, planLabel } from '../../planVocab';
import { StorePeople } from './StorePeople';
import { StoreChat } from './StoreChat';
import { StoreUpdates } from './StoreUpdates';

/**
 * One store, one surface.
 *
 * Everything about a store is edited here: what it is, what it pays for, who
 * is on it, what it may use, and its chat group. Before this the same store
 * was spread over three sections and four overlays, each asking "which
 * store?" again and answering it differently.
 *
 * The rule the layout follows: a block that belongs to a store opens IN
 * PLACE. Nothing about a store is behind a modal or a drawer.
 */

/**
 * How it's paid, and ACCESS, are two questions — not four options of one.
 *
 * `billing_active=false` is a different kind of thing from a trial or a comp:
 * it 403s every request the store's users make (EnsureMerchantAccess), and it
 * leaves the plan exactly as it was. Folding it in beside "Paying" invited an
 * admin to suspend an account while reaching for a billing state.
 *
 * LAPSED is deliberately absent: it is derived from plan_ends_at being in the
 * past, so it is shown, never chosen.
 */
const PAID_OPTIONS = [
  { key: 'trial', label: 'Free trial' },
  { key: 'paying', label: 'Paying' },
  { key: 'comped', label: 'Free of charge' },
];

const paidKey = (detail) => {
  if (!detail) return null;
  if (detail.comped) return 'comped';
  if (detail.on_trial) return 'trial';
  return 'paying';
};

const MODES = [
  { key: 'off', label: 'Off' },
  { key: 'shadow', label: 'Log only' },
  { key: 'enforce', label: 'Enforce' },
];

const asDate = (value) => (value ? String(value).slice(0, 10) : '');

const IInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.6v.1" />
  </svg>
);

export function StorePanel({ store, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const storeId = store?.id;

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      // ⚠️ `admin/ENTITLEMENTS/stores/{id}`, not `admin/stores/{id}`. Two
      // different route groups both expose "/stores": admin/stores holds the
      // console and creation, admin/entitlements holds a store's plan. This
      // was wrong on the first build and 404ed — which the error mapping
      // then reported as "That admin no longer exists", because the path
      // starts with /admin.
      const { data } = await axios.get(`/api/admin/entitlements/stores/${storeId}`);
      setDetail(data?.store || data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load this store.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (payload, { quiet } = {}) => {
    if (!storeId) return;
    setSaving(true);
    if (!quiet) setError('');
    try {
      const { data } = await axios.put(`/api/admin/entitlements/stores/${storeId}`, payload);
      setDetail(data?.store || data);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'That change did not save.');
    } finally {
      setSaving(false);
    }
  }, [storeId, onChanged]);

  if (loading && !detail) return <div className="rc-sc__panel"><Loading /></div>;

  if (!detail) {
    return (
      <div className="rc-sc__panel">
        <p className="rc-sc__error" role="alert">{error || 'Could not load this store.'}</p>
        <div><button type="button" className="rc-sc__btn" onClick={load}>Try again</button></div>
      </div>
    );
  }

  const assigned = detail.plan_stored ?? null;
  const paid = paidKey(detail);
  const lapsed = !!detail.plan_lapsed;
  const suspended = detail.billing_active === false;
  const addons = detail.addons || [];
  const overrides = detail.overrides || [];
  const seatMax = detail.usage?.limit_seats?.max ?? null;
  const featureLabel = (key) => (detail.catalog || []).find((f) => f.key === key)?.label || key;

  const stateChip = () => {
    if (suspended) return <span className="rc-sc__state rc-sc__state--bad">Suspended</span>;
    if (!assigned) return <span className="rc-sc__state">No plan</span>;
    if (lapsed) return <span className="rc-sc__state rc-sc__state--bad">Lapsed · {planLabel(detail.plan)}</span>;
    if (paid === 'paying') return <span className="rc-sc__state rc-sc__state--ok">Paying · {planLabel(detail.plan)}</span>;
    return <span className="rc-sc__state rc-sc__state--beta">Beta · {planLabel(detail.plan)}</span>;
  };

  const setPaid = (key) => {
    if (key === paid) return;
    // One field, three shapes underneath. Writing all three every time is
    // what stops a store being comped AND trialling at once.
    if (key === 'comped') save({ comped: true, trial_ends_at: null });
    else if (key === 'trial') save({ comped: false, trial_ends_at: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) });
    else save({ comped: false, trial_ends_at: null });
  };

  const toggleAddon = (addon) => {
    const next = addons.includes(addon) ? addons.filter((a) => a !== addon) : [...addons, addon];
    save({ addons: next });
  };

  return (
    <section className="rc-sc__panel" aria-label={detail.display_name || detail.name}>
      <div className="rc-sc__panel-head">
        <h3>{detail.display_name || detail.name || `Store ${detail.id}`}</h3>
        <span className="rc-sc__panel-id">#{detail.id}</span>
        <span className="rc-sc__spacer" />
        {saving && <span className="rc-sc__band-count">Saving…</span>}
        {stateChip()}
      </div>

      {error && <p className="rc-sc__error" role="alert">{error}</p>}

      {/* ── ACCOUNT ───────────────────────────────────────────────────── */}
      <div className="rc-sc__band">
        <div className="rc-sc__band-head"><h4>Account</h4></div>

        <div className="rc-sc__pair">
          <fieldset className="rc-sc__seg" style={{ display: 'block' }}>
            <legend>Plan</legend>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                aria-pressed={assigned === null}
                className={`rc-sc__opt-btn rc-sc__opt-btn--narrow ${assigned === null ? 'rc-sc__opt-btn--none' : ''}`}
                onClick={() => assigned !== null && save({ plan: null })}
              >
                None
              </button>
              {PLAN_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={assigned === option.value}
                  className={`rc-sc__opt-btn rc-sc__opt-btn--narrow ${assigned === option.value ? 'rc-sc__opt-btn--on' : ''}`}
                  onClick={() => assigned !== option.value && save({ plan: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="rc-sc__hint" style={{ marginTop: 9 }}>
              {assigned ? PLAN_BLURB[assigned] : 'No plan means no access to anything — once enforcement is on.'}
            </p>
          </fieldset>

          <fieldset className="rc-sc__seg" style={{ display: 'block' }} disabled={!assigned}>
            <legend>How it’s paid</legend>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', opacity: assigned ? 1 : 0.5 }}>
              {PAID_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={paid === option.key}
                  disabled={!assigned}
                  className={`rc-sc__opt-btn ${paid === option.key ? (option.key === 'paying' ? 'rc-sc__opt-btn--ok' : 'rc-sc__opt-btn--warn') : ''}`}
                  onClick={() => setPaid(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="rc-sc__hint" style={{ marginTop: 9 }}>
              {assigned
                ? 'Free trial and Free of charge are what beta means — a plan nobody pays for. Set by hand until Stripe is connected.'
                : 'Not a question until the store has a plan.'}
            </p>
          </fieldset>
        </div>

        {assigned && (
          <div className="rc-sc__tiles">
            <label className="rc-sc__tile">
              <span style={{ flex: '1 1 auto' }}>Started</span>
              <input
                type="date"
                className="rc-sc__date"
                value={asDate(detail.plan_started_at)}
                onChange={(e) => save({ plan_started_at: e.target.value || null })}
              />
            </label>
            <label className="rc-sc__tile">
              <span style={{ flex: '1 1 auto' }}>{paid === 'trial' ? 'Trial ends' : 'Renews'}</span>
              <input
                type="date"
                className="rc-sc__date"
                value={asDate(paid === 'trial' ? detail.trial_ends_at : detail.plan_ends_at)}
                onChange={(e) => save(paid === 'trial'
                  ? { trial_ends_at: e.target.value || null }
                  : { plan_ends_at: e.target.value || null })}
              />
            </label>
          </div>
        )}

        {lapsed && (
          <p className="rc-sc__note rc-sc__note--warn">
            <IInfo />
            <span>
              The renewal date has passed, so this store reads as <strong>lapsed</strong> and resolves to
              CORE. Move the date or change the plan — “lapsed” is not something you can pick.
            </span>
          </p>
        )}

        <div className="rc-sc__tile" style={{ gap: 12 }}>
          <span style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span>Access</span>
            <span className="rc-sc__hint">Suspending signs everyone out of this store. The plan is untouched.</span>
          </span>
          <div role="group" aria-label="Access" style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
            <button
              type="button"
              aria-pressed={!suspended}
              className={`rc-sc__opt-btn ${!suspended ? 'rc-sc__opt-btn--ok' : ''}`}
              style={{ flex: '0 0 auto', minHeight: 40 }}
              onClick={() => suspended && save({ billing_active: true })}
            >
              Active
            </button>
            <button
              type="button"
              aria-pressed={suspended}
              className={`rc-sc__opt-btn ${suspended ? 'rc-sc__opt-btn--none' : ''}`}
              style={{ flex: '0 0 auto', minHeight: 40 }}
              onClick={() => {
                if (suspended) return;
                if (window.confirm(`Suspend ${detail.display_name || detail.name}? Everyone on it is signed out until you switch it back.`)) {
                  save({ billing_active: false });
                }
              }}
            >
              Suspended
            </button>
          </div>
        </div>
      </div>

      {/* ── PEOPLE ────────────────────────────────────────────────────── */}
      <StorePeople storeId={detail.id} seatLimit={seatMax} onChanged={onChanged} />

      {/* ── WHAT THEY CAN USE ─────────────────────────────────────────── */}
      <div className="rc-sc__band">
        <div className="rc-sc__band-head">
          <h4>What they can use</h4>
          <span className="rc-sc__band-count">
            {addons.length} of {Object.keys(ADDON_LABELS).length} add-ons on
            {overrides.length ? ` · ${overrides.length} exception${overrides.length === 1 ? '' : 's'}` : ''}
          </span>
        </div>

        {!assigned && (
          <p className="rc-sc__note">
            <IInfo />
            <span>Add-ons and limits need a plan first — there is nothing for them to add to.</span>
          </p>
        )}

        <div className="rc-sc__addons">
          {Object.keys(ADDON_LABELS).map((addon) => {
            const on = addons.includes(addon);

            return (
              <button
                key={addon}
                type="button"
                aria-pressed={on}
                disabled={!assigned}
                className={`rc-sc__addon ${on ? 'rc-sc__addon--on' : ''}`}
                onClick={() => toggleAddon(addon)}
              >
                {ADDON_LABELS[addon]}
              </button>
            );
          })}
        </div>

        {assigned && (
          <div className="rc-sc__tiles">
            <LimitTile
              label="Monthly listings"
              feature="limit_listings"
              usage={detail.usage?.limit_listings}
              override={overrides.find((o) => o.feature === 'limit_listings')}
              onSave={(value) => save({ overrides: { limit_listings: value } })}
            />
            <LimitTile
              label="Admin seats"
              feature="limit_seats"
              usage={detail.usage?.limit_seats}
              override={overrides.find((o) => o.feature === 'limit_seats')}
              onSave={(value) => save({ overrides: { limit_seats: value } })}
            />
          </div>
        )}

        <details className="rc-sc__fold">
          <summary>
            Exceptions and enforcement
            <span className="rc-sc__fold-sub">
              {overrides.length
                ? `${overrides.length} feature${overrides.length === 1 ? '' : 's'} set against the plan`
                : 'no exceptions'}
              {' · '}
              {detail.effective_mode === 'enforce' ? 'enforced' : detail.effective_mode === 'shadow' ? 'logging only' : 'enforcement off'}
            </span>
          </summary>
          <div className="rc-sc__fold-body">
            {overrides.length === 0 ? (
              <p className="rc-sc__hint">
                Nothing is set against the plan. Exceptions are added from the Feature Matrix, which is
                where the list of features lives; they are removed here.
              </p>
            ) : (
              overrides.map((o) => (
                <div className="rc-sc__tile" key={o.feature}>
                  {/* The catalogue carries the human name; the slug is what
                      the database stores and is not for reading. */}
                  <span style={{ flex: '1 1 200px', minWidth: 0 }}>{featureLabel(o.feature)}</span>
                  <span className={`rc-sc__pill ${o.value ? '' : 'rc-sc__pill--bad'}`}>
                    {o.value ? 'Forced on' : 'Forced off'}
                  </span>
                  <button
                    type="button"
                    className="rc-sc__btn rc-sc__btn--sm"
                    onClick={() => save({ overrides: { [o.feature]: null } })}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}

            <fieldset className="rc-sc__seg" style={{ display: 'block' }}>
              <legend>Enforcement for this store</legend>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {MODES.map((m) => {
                  const on = (detail.entitlements_mode ?? null) === m.key;

                  return (
                    <button
                      key={m.key}
                      type="button"
                      aria-pressed={on}
                      className={`rc-sc__opt-btn ${on ? 'rc-sc__opt-btn--none' : ''}`}
                      onClick={() => save({ entitlements_mode: m.key })}
                    >
                      {m.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  aria-pressed={(detail.entitlements_mode ?? null) === null}
                  className={`rc-sc__opt-btn ${(detail.entitlements_mode ?? null) === null ? 'rc-sc__opt-btn--none' : ''}`}
                  onClick={() => save({ entitlements_mode: null })}
                >
                  Follow the platform
                </button>
              </div>
              <p className="rc-sc__hint" style={{ marginTop: 9 }}>
                The platform is <strong>{detail.effective_mode === 'enforce' ? 'enforcing' : detail.effective_mode === 'shadow' ? 'logging only' : 'off'}</strong>.
                A store can only be stricter than the platform, never looser.
              </p>
            </fieldset>
          </div>
        </details>
      </div>

      {/* ── CHAT GROUP, and the platform channel this store hears ────── */}
      <StoreChat store={store} onChanged={onChanged}>
        <StoreUpdates storeId={detail.id} />
      </StoreChat>
    </section>
  );
}

/**
 * A limit, saying whether the number shown is the PLAN's or one somebody
 * typed. Without that an admin cannot tell what clearing the field would do.
 */
function LimitTile({ label, usage, override, onSave }) {
  const planMax = usage?.max ?? null;
  const [value, setValue] = useState(override ? String(override.value) : (planMax ?? ''));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(override ? String(override.value) : (planMax ?? ''));
    setDirty(false);
  }, [override?.value, planMax]);

  const commit = () => {
    if (!dirty) return;
    const trimmed = String(value).trim();
    onSave(trimmed === '' ? null : Number(trimmed));
    setDirty(false);
  };

  return (
    <div className="rc-sc__tile">
      <span style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span>{label}</span>
        <span className="rc-sc__hint">
          {override
            ? `set for this store — the plan gives ${planMax ?? 'no limit'}`
            : 'from the plan'}
        </span>
      </span>
      <span style={{ color: 'var(--sc-faint)' }}>{usage?.used ?? 0} /</span>
      <label>
        <span className="rc-sc__vh">{`${label} limit`}</span>
        <input
          className="rc-sc__num"
          type="text"
          inputMode="numeric"
          value={value ?? ''}
          placeholder="∞"
          onChange={(e) => { setValue(e.target.value); setDirty(true); }}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
        />
      </label>
    </div>
  );
}

export default StorePanel;
